import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { APP_CONFIG } from '../config/app';

const PASSWORD_KEY = 'downUnderDynasty_authenticated';
const CORRECT_PASSWORD = 'pero';

interface PasswordGateProps {
  children: React.ReactNode;
}

const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(true);

  // Check if password gate is enabled
  const isPasswordGateEnabled = APP_CONFIG.ENABLE_PASSWORD_GATE;

  // Check if user is already authenticated on mount
  useEffect(() => {
    if (!isPasswordGateEnabled) {
      setIsAuthenticated(true);
      setChecking(false);
      return;
    }
    
    const authenticated = localStorage.getItem(PASSWORD_KEY) === 'true';
    setIsAuthenticated(authenticated);
    setChecking(false);
  }, [isPasswordGateEnabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);

    if (password === CORRECT_PASSWORD) {
      localStorage.setItem(PASSWORD_KEY, 'true');
      setIsAuthenticated(true);
      setPassword('');
    } else {
      setError(true);
      setPassword('');
    }
  };

  // Show nothing while checking authentication status
  if (checking) {
    return null;
  }

  // If password gate is disabled or authenticated, show the app
  if (!isPasswordGateEnabled || isAuthenticated) {
    return <>{children}</>;
  }

  // Show password gate
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
          Down Under Dynasty
        </Typography>
        
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Please enter the password to access the site.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
            }}
            error={error}
            autoFocus
            sx={{ mb: error ? 1 : 2 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e);
              }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Incorrect password. Please try again.
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 1,
              py: 1.5,
            }}
          >
            Enter
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default PasswordGate;

